import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, XCircle, Minus, Sparkles, BarChart3, Target, Wallet } from 'lucide-react';

const budgetOptions = [
  { value: 'low', label: 'Low / Public Universities', desc: 'Under 5 Lakh INR/year' },
  { value: 'mid', label: 'Mid-Range', desc: '5-15 Lakh INR/year' },
  { value: 'high', label: 'Premium / Top-Ranked', desc: '15+ Lakh INR/year' },
];

const careerOptions = [
  { value: 'tech', label: 'Tech / IT' },
  { value: 'engineering', label: 'Engineering' },
  { value: 'healthcare', label: 'Healthcare / Nursing' },
  { value: 'management', label: 'Management / MBA' },
];

const priorityOptions = [
  { value: 'cost', label: 'Lower Cost', icon: Wallet },
  { value: 'pr', label: 'Faster PR Pathway', icon: Target },
  { value: 'psw', label: 'Longer Post-Study Work', icon: BarChart3 },
];

// Comparison data matrix
const countryData = {
  germany: {
    name: 'Germany',
    flag: 'DE',
    color: '#003087',
    tuition: { low: '0 EUR (Public)', mid: '5,000-15,000 EUR', high: '15,000-30,000 EUR' },
    livingCost: '10,000-12,000 EUR/year',
    pswDuration: '18 months',
    prPathway: 'Work 2 years → PR eligible',
    prDifficulty: 'Moderate',
    language: 'German helpful (English programs available)',
    mastersDuration: '2 years',
    topSectors: {
      tech: { rating: 5, note: 'Berlin & Munich are major tech hubs' },
      engineering: { rating: 5, note: 'World leader in mechanical & automotive engineering' },
      healthcare: { rating: 4, note: 'Growing demand for healthcare workers' },
      management: { rating: 3, note: 'Strong in industrial management' },
    },
    budgetScore: { low: 5, mid: 4, high: 3 },
    prScore: 4,
    pswScore: 3,
    highlights: [
      'Zero tuition at public universities',
      '18-month job seeker visa post-study',
      'Europe\'s largest economy',
      'Ausbildung (vocational training) option',
      'Blocked account: ~11,208 EUR/year',
    ],
    limitations: [
      'German language needed for many jobs',
      'Shorter PSW than UK/Canada',
      'Bureaucratic processes',
    ],
  },
  uk: {
    name: 'United Kingdom',
    flag: 'GB',
    color: '#001F5C',
    tuition: { low: '12,000-15,000 GBP', mid: '15,000-25,000 GBP', high: '25,000-45,000 GBP' },
    livingCost: '12,000-15,000 GBP/year',
    pswDuration: '2 years (Graduate Route)',
    prPathway: 'Employer sponsorship → ILR (5 years)',
    prDifficulty: 'Hard',
    language: 'English (native)',
    mastersDuration: '1 year',
    topSectors: {
      tech: { rating: 4, note: 'London is a global fintech & tech hub' },
      engineering: { rating: 4, note: 'Strong aerospace & civil engineering' },
      healthcare: { rating: 5, note: 'NHS has high demand for nurses & doctors' },
      management: { rating: 5, note: 'World-class MBA programs (LBS, Oxford, Cambridge)' },
    },
    budgetScore: { low: 2, mid: 3, high: 5 },
    prScore: 2,
    pswScore: 4,
    highlights: [
      '1-year masters saves time & money',
      '2-year Graduate Route (PSW) visa',
      '4 of world\'s top 10 universities',
      'No language barrier — all English',
      'Global career network & alumni',
    ],
    limitations: [
      'Higher tuition fees',
      'PR pathway requires employer sponsorship',
      'High cost of living in London',
    ],
  },
  canada: {
    name: 'Canada',
    flag: 'CA',
    color: '#0052CC',
    tuition: { low: '7,000-15,000 CAD', mid: '15,000-30,000 CAD', high: '30,000-55,000 CAD' },
    livingCost: '10,000-15,000 CAD/year',
    pswDuration: 'Up to 3 years (PGWP)',
    prPathway: 'Express Entry / PNP (1-2 years work)',
    prDifficulty: 'Moderate-Easy',
    language: 'English / French',
    mastersDuration: '1.5-2 years',
    topSectors: {
      tech: { rating: 5, note: 'Toronto, Vancouver & Montreal are booming tech hubs' },
      engineering: { rating: 4, note: 'Strong demand in oil, mining & civil engineering' },
      healthcare: { rating: 5, note: 'Massive shortage of healthcare workers' },
      management: { rating: 4, note: 'Growing business ecosystem & startup scene' },
    },
    budgetScore: { low: 3, mid: 4, high: 4 },
    prScore: 5,
    pswScore: 5,
    highlights: [
      'Up to 3-year Post-Graduation Work Permit',
      'Clear PR pathway via Express Entry & PNP',
      'Can work 20 hrs/week during study',
      'Multicultural & welcoming society',
      'Spouse can get open work permit',
    ],
    limitations: [
      'Tuition rising in recent years',
      'Cold winters in many regions',
      'SDS visa stream has specific requirements',
    ],
  },
  australia: {
    name: 'Australia',
    flag: 'AU',
    color: '#0891B2',
    tuition: { low: '20,000-25,000 AUD', mid: '25,000-35,000 AUD', high: '35,000-55,000 AUD' },
    livingCost: '21,000-27,000 AUD/year',
    pswDuration: '2-4 years (Subclass 485)',
    prPathway: 'Points-based (189/190/491) after 1-2 years work',
    prDifficulty: 'Moderate',
    language: 'English (native)',
    mastersDuration: '1.5-2 years',
    topSectors: {
      tech: { rating: 4, note: 'Sydney & Melbourne are growing tech hubs' },
      engineering: { rating: 5, note: 'Mining, civil & environmental engineering in high demand' },
      healthcare: { rating: 5, note: 'Critical shortage of nurses, doctors & aged care workers' },
      management: { rating: 4, note: 'Strong business programs at Go8 universities' },
    },
    budgetScore: { low: 2, mid: 3, high: 4 },
    prScore: 4,
    pswScore: 4,
    highlights: [
      'Up to 4-year post-study work visa',
      'Transparent points-based PR system',
      'Work 48 hrs/fortnight during study',
      '7 of world\'s top 100 universities',
      'High quality of life & safety',
    ],
    limitations: [
      'Higher tuition than Germany/Canada',
      'High cost of living in Sydney/Melbourne',
      'Points threshold can change annually',
    ],
  },
  ireland: {
    name: 'Ireland',
    flag: 'IE',
    color: '#16A34A',
    tuition: { low: '12,000-15,000 EUR', mid: '15,000-22,000 EUR', high: '22,000-35,000 EUR' },
    livingCost: '8,000-12,000 EUR/year',
    pswDuration: '2 years (Third Level Graduate)',
    prPathway: 'Critical Skills Permit → 5 years → PR',
    prDifficulty: 'Moderate',
    language: 'English (native)',
    mastersDuration: '1-2 years',
    topSectors: {
      tech: { rating: 5, note: 'Dublin is EU tech capital — Google, Apple, Meta HQs' },
      engineering: { rating: 3, note: 'Growing demand in pharma & biotech engineering' },
      healthcare: { rating: 5, note: 'Severe nursing & healthcare worker shortage' },
      management: { rating: 4, note: 'Strong fintech & business analytics programs' },
    },
    budgetScore: { low: 3, mid: 4, high: 3 },
    prScore: 3,
    pswScore: 4,
    highlights: [
      '2-year stay-back visa for graduates',
      'English-speaking EU country',
      'EU\'s tech & pharma hub',
      '90%+ graduate employment rate',
      'Lower cost than UK',
    ],
    limitations: [
      'Smaller job market than UK/Germany',
      'Housing crisis in Dublin',
      'Fewer university options than UK',
    ],
  },
};

const RatingBar = ({ score, max = 5, color }) => (
  <div className="flex gap-0.5">
    {Array.from({ length: max }).map((_, i) => (
      <div
        key={`rating-${color}-${i}`}
        className="h-2 w-6 rounded-sm"
        style={{ background: i < score ? color : '#e2e8f0' }}
      />
    ))}
  </div>
);

const CompareDestinationsPage = () => {
  const [budget, setBudget] = useState('');
  const [career, setCareer] = useState('');
  const [priority, setPriority] = useState('');
  const [showResults, setShowResults] = useState(false);

  const handleCompare = () => {
    if (budget && career && priority) setShowResults(true);
  };

  const getOverallScore = (country) => {
    const c = countryData[country];
    let score = 0;
    // Budget match
    score += (c.budgetScore[budget] || 3) * 2;
    // Career match
    score += (c.topSectors[career]?.rating || 3) * 2;
    // Priority match
    if (priority === 'cost') score += c.budgetScore[budget] || 3;
    if (priority === 'pr') score += c.prScore;
    if (priority === 'psw') score += c.pswScore;
    return score;
  };

  const rankedCountries = showResults
    ? Object.keys(countryData)
        .map((key) => ({ key, ...countryData[key], score: getOverallScore(key) }))
        .sort((a, b) => b.score - a.score)
    : [];

  return (
    <div data-testid="sa-compare-page">
      {/* Hero */}
      <section className="sa-hero-gradient relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-10 w-80 h-80 bg-[#C5A028] rounded-full blur-[120px]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-14 lg:py-20 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5 mb-6">
            <Sparkles className="w-3.5 h-3.5 text-[#C5A028]" />
            <span className="text-xs text-white/90 font-medium">Interactive Tool</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold font-syne text-white leading-tight mb-4">
            Compare <span className="text-[#C5A028]">Study Destinations</span>
          </h1>
          <p className="text-base text-slate-300 max-w-2xl mx-auto">
            Germany vs UK vs Canada vs Australia vs Ireland — find the best country for your profile,
            budget, and career goals in 30 seconds.
          </p>
        </div>
      </section>

      {/* Filter Form */}
      <section className="py-12 lg:py-16 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <div className="bg-white border border-slate-200 rounded-2xl p-6 lg:p-8 shadow-sm" data-testid="sa-compare-form">
            <h2 className="text-lg font-bold font-syne text-[#003087] mb-6">
              Tell Us About Your Preferences
            </h2>

            {/* Budget */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-slate-700 mb-3">
                <Wallet className="w-4 h-4 inline mr-1.5 text-[#003087]" /> Budget Range
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {budgetOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setBudget(opt.value)}
                    className={`p-3 rounded-xl border-2 text-left transition-all ${
                      budget === opt.value
                        ? 'border-[#003087] bg-[#003087]/5'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`budget-${opt.value}`}
                  >
                    <div className="text-sm font-semibold text-slate-900">{opt.label}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{opt.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Career Goal */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-slate-700 mb-3">
                <Target className="w-4 h-4 inline mr-1.5 text-[#003087]" /> Primary Career Goal
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {careerOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setCareer(opt.value)}
                    className={`p-3 rounded-xl border-2 text-center transition-all ${
                      career === opt.value
                        ? 'border-[#003087] bg-[#003087]/5'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`career-${opt.value}`}
                  >
                    <div className="text-sm font-semibold text-slate-900">{opt.label}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Core Priority */}
            <div className="mb-8">
              <label className="block text-sm font-semibold text-slate-700 mb-3">
                <BarChart3 className="w-4 h-4 inline mr-1.5 text-[#003087]" /> Core Priority
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {priorityOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setPriority(opt.value)}
                    className={`p-3 rounded-xl border-2 flex items-center gap-3 transition-all ${
                      priority === opt.value
                        ? 'border-[#003087] bg-[#003087]/5'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`priority-${opt.value}`}
                  >
                    <opt.icon className="w-5 h-5 text-[#003087] shrink-0" />
                    <div className="text-sm font-semibold text-slate-900">{opt.label}</div>
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={handleCompare}
              disabled={!budget || !career || !priority}
              className="w-full bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold text-sm py-3.5 rounded-full transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="sa-compare-submit"
            >
              Compare Destinations
            </button>
          </div>
        </div>
      </section>

      {/* Results */}
      {showResults && (
        <section className="pb-16 lg:pb-24 bg-white" data-testid="sa-compare-results">
          <div className="max-w-7xl mx-auto px-6">
            {/* Ranked Banner */}
            <div className="text-center mb-10">
              <div className="sa-gold-line mx-auto mb-4" />
              <h2 className="text-2xl sm:text-3xl font-bold font-syne text-[#003087] mb-2">
                Your Personalized Comparison
              </h2>
              <p className="text-sm text-slate-500">
                Based on: {budgetOptions.find((b) => b.value === budget)?.label} budget
                &bull; {careerOptions.find((c) => c.value === career)?.label} career
                &bull; {priorityOptions.find((p) => p.value === priority)?.label} priority
              </p>
            </div>

            {/* Top Pick Badge */}
            <div className="bg-gradient-to-r from-[#003087] to-[#001F5C] rounded-2xl p-6 mb-8 flex flex-col sm:flex-row items-center gap-4">
              <div className="text-3xl">{getFlagEmoji(rankedCountries[0].flag)}</div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="bg-[#C5A028] text-black text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">
                    Best Match
                  </span>
                </div>
                <h3 className="text-xl font-bold font-syne text-white mt-1">
                  {rankedCountries[0].name} is your top pick!
                </h3>
                <p className="text-sm text-slate-300 mt-0.5">
                  Scores highest for your profile based on budget, career, and priority preferences.
                </p>
              </div>
              <Link
                to="/study-abroad/book-counselling/"
                className="shrink-0 sm:ml-auto inline-flex items-center gap-2 bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold text-xs px-6 py-2.5 rounded-full transition-all"
                data-testid="sa-compare-top-cta"
              >
                Book Free Counselling <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Side-by-Side Comparison Cards */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {rankedCountries.map((country, idx) => (
                <div
                  key={country.key}
                  className={`rounded-2xl border-2 overflow-hidden ${
                    idx === 0 ? 'border-[#C5A028] shadow-lg shadow-[#C5A028]/10' : 'border-slate-200'
                  }`}
                  data-testid={`compare-card-${country.key}`}
                >
                  {/* Header */}
                  <div className="p-5" style={{ background: country.color }}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{getFlagEmoji(country.flag)}</span>
                        <h3 className="text-lg font-bold font-syne text-white">{country.name}</h3>
                      </div>
                      {idx === 0 && (
                        <span className="bg-[#C5A028] text-black text-[9px] font-bold px-2 py-0.5 rounded-full uppercase">
                          #1 Match
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="p-5 space-y-4">
                    {/* Key Metrics */}
                    <div className="space-y-3">
                      <div>
                        <div className="text-xs text-slate-500 mb-1">Tuition Range</div>
                        <div className="text-sm font-semibold text-slate-900">{country.tuition[budget]}</div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-500 mb-1">Living Cost</div>
                        <div className="text-sm font-semibold text-slate-900">{country.livingCost}</div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-500 mb-1">Post-Study Work Visa</div>
                        <div className="text-sm font-semibold text-slate-900">{country.pswDuration}</div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-500 mb-1">PR Pathway</div>
                        <div className="text-sm font-semibold text-slate-900">{country.prPathway}</div>
                        <div className="text-xs text-slate-400">Difficulty: {country.prDifficulty}</div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-500 mb-1">Masters Duration</div>
                        <div className="text-sm font-semibold text-slate-900">{country.mastersDuration}</div>
                      </div>
                    </div>

                    {/* Career Match */}
                    <div className="bg-slate-50 rounded-xl p-4">
                      <div className="text-xs font-bold text-slate-700 mb-1.5 uppercase tracking-wider">
                        {careerOptions.find((c) => c.value === career)?.label} Career Fit
                      </div>
                      <RatingBar score={country.topSectors[career]?.rating || 3} color={country.color} />
                      <div className="text-xs text-slate-500 mt-1.5">{country.topSectors[career]?.note}</div>
                    </div>

                    {/* Highlights */}
                    <div>
                      <div className="text-xs font-bold text-slate-700 mb-2 uppercase tracking-wider">Highlights</div>
                      <div className="space-y-1.5">
                        {country.highlights.map((h) => (
                          <div key={h} className="flex items-start gap-2">
                            <CheckCircle className="w-3.5 h-3.5 text-green-500 shrink-0 mt-0.5" />
                            <span className="text-xs text-slate-700">{h}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Limitations */}
                    <div>
                      <div className="text-xs font-bold text-slate-700 mb-2 uppercase tracking-wider">Considerations</div>
                      <div className="space-y-1.5">
                        {country.limitations.map((l) => (
                          <div key={l} className="flex items-start gap-2">
                            <Minus className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" />
                            <span className="text-xs text-slate-600">{l}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* CTA */}
                    <Link
                      to={`/study-abroad/${country.key}/`}
                      className="block text-center text-sm font-semibold text-[#003087] hover:text-[#C5A028] pt-2 transition-colors"
                    >
                      Explore {country.name} &rarr;
                    </Link>
                  </div>
                </div>
              ))}
            </div>

            {/* Bottom CTA */}
            <div className="mt-12 bg-gradient-to-r from-[#003087] to-[#001F5C] rounded-2xl p-8 text-center">
              <h3 className="text-xl font-bold font-syne text-white mb-2">
                Need Help Deciding?
              </h3>
              <p className="text-sm text-slate-300 mb-6 max-w-lg mx-auto">
                Our certified counsellors have helped 10,000+ students choose the right
                destination. Get a personalized recommendation based on your complete profile.
              </p>
              <Link
                to="/study-abroad/book-counselling/"
                className="inline-flex items-center gap-2 bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold text-sm px-8 py-3.5 rounded-full transition-all sa-pulse-cta"
                data-testid="sa-compare-bottom-cta"
              >
                Book Free Counselling <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

function getFlagEmoji(countryCode) {
  const codePoints = countryCode.toUpperCase().split('').map((c) => 127397 + c.charCodeAt());
  return String.fromCodePoint(...codePoints);
}

export default CompareDestinationsPage;
